import {Product} from 'components/customer/Product/entity/Product';
import React from 'react';
import styled from 'styled-components';

interface SpecsProps {
    product: Product;
}

type DimensionSpec = {
    title: string;
    value: number | string;
};

const isDimensionAvailable = (value: unknown): value is number | string => {
    if (value === null || typeof value === 'undefined') return false;
    if (typeof value === 'number') return !isNaN(value) && value > 0;
    if (typeof value === 'string') return value.trim() !== '' && value !== '0';

    return false;
};

const getFirstAvailableDimension = (
    ...values: unknown[]
): number | string | undefined => {
    return values.find(isDimensionAvailable);
};

type DimensionFieldConfig = {
    label: string;
    getValue: (product: Product) => number | string | undefined;
};

const DIMENSION_FIELD_MAP: Record<string, DimensionFieldConfig> = {
    cabinet_height: {
        label: 'Height',
        getValue: (product) =>
            getFirstAvailableDimension(product.cabinet_height, product.height),
    },
    cabinet_width: {
        label: 'Width',
        getValue: (product) =>
            getFirstAvailableDimension(product.cabinet_width, product.width),
    },
    cabinet_depth: {
        label: 'Depth',
        getValue: (product) =>
            getFirstAvailableDimension(product.cabinet_depth, product.depth),
    },
    cabinet_right_width: {
        label: 'Right Width',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_right_width,
                product.width
            ),
    },
    cabinet_left_width: {
        label: 'Left Width',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_left_width,
                product.width2
            ),
    },
    cabinet_right_depth: {
        label: 'Right Depth',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_right_depth,
                product.cabinet_length_depth
            ),
    },
    cabinet_left_depth: {
        label: 'Left Depth',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_left_depth,
                product.cabinet_width_depth
            ),
    },
    cabinet_panel_width: {
        label: 'Width',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_panel_width,
                product.width
            ),
    },
    cabinet_panel_height: {
        label: 'Height',
        getValue: (product) => product.height,
    },
    cabinet_applied_panel_depth: {
        label: 'Depth',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_applied_panel_depth,
                product.applied_panel_depth
            ),
    },
    cabinet_upper_filler_depth: {
        label: 'Depth',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_upper_filler_depth,
                product.upper_filler_depth
            ),
    },
    cabinet_width_door_1: {
        label: 'Width 1',
        getValue: (product) =>
            getFirstAvailableDimension(
                product.cabinet_width_door_1,
                product.width
            ),
    },
    cabinet_width_door_2: {
        label: 'Width 2',
        getValue: (product) => product.width2,
    },
    cabinet_width_depth: {
        label: 'Left Depth',
        getValue: (product) => product.cabinet_width_depth,
    },
    cabinet_length_depth: {
        label: 'Right Depth',
        getValue: (product) => product.cabinet_length_depth,
    },
};

const getDimensionsFromFields = (product: Product): DimensionSpec[] => {
    // TODO: Refactor this to use the PDF form fields ...
    // Needs to dynamically update existing width/depth to prefix with Left * when * Right is present ...
    const dimensionFields = product.dimension_fields ?? [];
    const fieldMappings = dimensionFields.reduce<DimensionSpec[]>(
        (dimensions, field) => {
            const config = DIMENSION_FIELD_MAP[String(field)];
            if (!config) return dimensions;

            const value = config.getValue(product);
            if (!isDimensionAvailable(value)) return dimensions;

            dimensions.push({
                title: config.label,
                value,
            });

            return dimensions;
        },
        []
    );

    if (
        dimensionFields.includes('cabinet_width_depth') &&
        dimensionFields.includes('cabinet_length_depth')
    ) {
        // L-Shape component product (key shape) ...
        fieldMappings[1].title = 'Left Width'; // 'width'
        fieldMappings[0].title = 'Right Width'; // 'height'

        return [
            // Just use the raw index to rewrite the field order ...
            fieldMappings[1],
            fieldMappings[0],
            fieldMappings[2],
            fieldMappings[3],
        ];
    }

    return fieldMappings;
};

const getProductDimensions = (product: Product): DimensionSpec[] => {
    const fieldDimensions = getDimensionsFromFields(product);
    if (fieldDimensions.length) {
        return fieldDimensions;
    }

    return [
        {title: 'Height', value: product.cabinet_height ?? product.height},
        {title: 'Width', value: product.cabinet_width ?? product.width},
        {title: 'Depth', value: product.cabinet_depth ?? product.depth},
    ].filter(({value}) => isDimensionAvailable(value)) as DimensionSpec[];
};

export const Specs = ({product}: SpecsProps) => {
    const dimensions = getProductDimensions(product);

    return (
        <Container>
            <Quantity>
                <Header>Specs:</Header>
                <Title>Quantity</Title>
                <Text>{product.quantity}</Text>
            </Quantity>
            {dimensions.length ? (
                <Dimensions>
                    {dimensions.map(({title, value}) => (
                        <Dimension key={title}>
                            <Title>{title}</Title>
                            <Text>{value}</Text>
                        </Dimension>
                    ))}
                </Dimensions>
            ) : null}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Quantity = styled.div`
    display: flex;
    flex-direction: column;
`;

const Dimensions = styled.div`
    display: flex;
    gap: 5px;
    justify-content: space-between;
    flex-wrap: wrap;
`;

const Dimension = styled.div`
    display: flex;
    flex-direction: column;
`;

const Text = styled.div`
    text-transform: uppercase;
    font-size: 0.8em;
    color: black;
    line-height: 14px;
    font-weight: 500;
    white-space: nowrap;
`;

const Header = styled(Text)`
    color: ${({theme}) => theme.colors.secondary?.main};
    font-weight: 800;
`;

const Title = styled(Text)`
    color: gray;
    font-weight: bold;
`;
